import {
    Anchor,
    Breadcrumbs,
    Group,
    Stack,
} from '@mantine/core'
import IonIcon from '@reacticons/ionicons'
import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { getEntity, getEntities } from 'app/state/ducks/entities/selectors'
import { loadOneEntity, loadEntities } from 'app/state/ducks/entities/thunks'
import { loadContacts } from 'app/state/ducks/contacts/thunks'
import { loadWireInstructions } from 'app/state/ducks/wire-instructions/thunks'
import { loadAllEntityProfiles } from 'app/state/ducks/entity-profile/thunks'
import { loadAllEntityTypes } from 'app/state/ducks/entity-type/thunks'
import { loadAllCompanies } from 'app/state/ducks/companies/thunks'
import { EntitySearch } from 'app/views/components/search/entity-search'
import { ContractType } from 'app/models/common-types'
import EntityHeader from './entity-header'
import { loadNotesByContract } from 'app/state/ducks/notes/thunks'
import EntityActions from '../entity-actions'
import SegmentControlUpdated from 'app/views/components/segments/segment-control-updated'
import ContactsPage from '../entity-contacts/contacts-page'
import WireInstructionsPage from '../entity-wire-instructions/wire-instructions-page'
import PositionsPage from '../entity-positions/positions-page'
import EntityPage from './entity-page'
import NotesPage from 'app/views/components/notes-and-attachment/notes-page'
import AttachmentsPage from 'app/views/components/notes-and-attachment/attachments-page'


interface props {
    tab: string
}

export function EntityDrillPage({ tab }: props) {
    const { entityId, status } = useParams()

    const tabs: { [key in props['tab']]: { route: string; label: string; active: boolean; pageRender: JSX.Element } } = {
        entityBase: { route: `/entitymanagement/entity/${entityId}/${status}`, label: 'OVERVIEW', active: tab === 'entityBase' ? true : false, pageRender: <EntityPage /> },
        entityContacts: { route: `/entitymanagement/contacts/${entityId}/${status}`, label: 'CONTACTS', active: tab === 'entityContacts' ? true : false, pageRender: <ContactsPage /> },
        entityWI: { route: `/entitymanagement/wireinstructions/${entityId}/${status}`, label: 'WIRE INSTRUCTIONS', active: tab === 'entityWI' ? true : false, pageRender: <WireInstructionsPage /> },
        entityPositions: { route: `/entitymanagement/positions/${entityId}/${status}`, label: 'POSITIONS', active: tab === 'entityPositions' ? true : false, pageRender: <PositionsPage /> },
        entityNotes: { route: `/entitymanagement/entity/${entityId}/${status}/notes`, label: 'NOTES', active: tab === 'entityNotes' ? true : false, pageRender: <NotesPage /> },
        entityAttachments: { route: `/entitymanagement/entity/${entityId}/${status}/attachments`, label: 'ATTACHMENTS', active: tab === 'entityAttachments' ? true : false, pageRender: <AttachmentsPage /> },
    }

    const dispatch = useDispatch()
    const navigate = useNavigate()
    const entity = useSelector(getEntity)
    const entities = useSelector(getEntities)

    const items = [
        { title: 'Entity Management', href: '/entitymanagement' },
        { title: entity?.entityName },
    ].map((item, index) => (
        <Anchor
            onClick={item.href ? () => navigate(item.href) : () => null}
            key={index}
            color="#374151"
            fw={500}
            fz="14px"
        >
            {item.title}
        </Anchor>
    ))

    useEffect(() => {
        if (entityId) {
            dispatch(loadOneEntity(entityId, status ?? 'Inactive'))
            dispatch(loadNotesByContract(entityId, ContractType.Entities))
        }
    }, [entityId, dispatch])

    // Tenant-wide lookups: none of these depend on which entity is shown, so
    // they load once per mount, not again on every entity switch. The header's
    // Edit prefills the entity's contacts and wire instructions from the store;
    // only the Contacts and Wire tabs used to load them.
    useEffect(() => {
        dispatch(loadEntities())
        dispatch(loadAllEntityProfiles())
        dispatch(loadAllEntityTypes())
        dispatch(loadAllCompanies())
        dispatch(loadContacts())
        dispatch(loadWireInstructions())
    }, [dispatch])

    return (
        <Stack w="100%">
            <Breadcrumbs
                separator={<IonIcon name="chevron-forward-outline" />}
            >
                {items}
            </Breadcrumbs>
            <EntityHeader entity={entity} />
            <Group w="100%" position="apart" className='tabsGapsActivityWithEntitySearch'>
                <Group position="right">
                    <SegmentControlUpdated tabs={tabs} />
                </Group>
                <Group noWrap className="searchActionsGroup" align="center">
                    <EntitySearch
                        data={entities}
                        placeholder="Entity Search"
                        width={300}
                        dropdownWidth={570}
                        transformX={170}
                    />
                    <EntityActions row={entity} primaryInput={true} extended withRedirection />
                </Group>
            </Group>
            <Group noWrap w='100%' mt='10px'>
                {tabs[tab].pageRender}
            </Group>
        </Stack>
    )
}